(() => {
  'use strict';
  if (window.MFNavigationTracking) return;
  const configNode = document.getElementById('mf-navigation-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const main = document.querySelector('main');
  const sections = new Map();
  const seen = new Set();
  const timers = new Map();
  const events = [];
  const openedSearch = new WeakSet();
  let homeRecorded = false;
  let observer;
  let scanTimer;
  const preview = window.Shopify?.theme?.role !== 'main' || window.Shopify?.designMode === true;
  const state = { version: '1', preview, sent: 0, blocked: 0, unavailable: 0, events };
  window.MFNavigationTracking = state;

  function allowed() {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
    catch { return false; }
  }

  function safePath(value) {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin) return null;
      const path = url.pathname.replace(/\/$/, '') || '/';
      if (path === '/' || path === '/search') return path;
      return /^\/(collections|products|pages|blogs)\/[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_-]+)?$/.test(path)
        && path.length <= 180 ? path : null;
    } catch { return null; }
  }

  function id(value) {
    return String(value || 'none').replace(/[^a-zA-Z0-9_.:/-]/g, '').slice(0, 100) || 'none';
  }

  function context() {
    return config.homepage ? 'homepage' : location.pathname === '/search' ? 'search' : 'other';
  }

  function emit(name, detail = {}) {
    if (!allowed()) { state.blocked += 1; return false; }
    const params = {
      mf_nav_area: detail.area || 'site',
      mf_nav_section: id(detail.section),
      mf_nav_item: id(detail.item),
      mf_nav_target: detail.target || 'none',
      mf_nav_context: context(),
      mf_nav_version: '1',
      mf_nav_position: detail.position || 0,
      send_to: config.measurementId,
      page_location: location.origin + (safePath(location.href) || '/'),
      page_referrer: safePath(document.referrer) ? location.origin + safePath(document.referrer) : '',
      page_title: 'Morgan & French — ' + context(),
      transport_type: 'beacon'
    };
    if (!preview && typeof window.gtag !== 'function') { state.unavailable += 1; return false; }
    if (preview) {
      events.push({ name, params });
      if (events.length > 100) events.shift();
      document.dispatchEvent(new CustomEvent('mf:navigation-preview', { detail: { name, params } }));
    } else {
      try { window.gtag('event', name, params); }
      catch { state.unavailable += 1; return false; }
      state.sent += 1;
    }
    return true;
  }

  function area(section) {
    if (section.dataset.mfNavArea) return section.dataset.mfNavArea;
    if (section.classList.contains('section-hero-slider')) return 'homepage_hero';
    if (section.id === 'shopify-section-mf-cr-discovery' || section.classList.contains('section-collection-4x')
      || section.classList.contains('section-collection-ranges')) return 'homepage_categories';
    return 'homepage_sections';
  }

  function sectionFor(element) {
    return element.closest('[data-mf-nav-section],.shopify-section');
  }

  function sectionId(section) {
    return id(section?.dataset.mfNavSection || section?.id?.replace(/^shopify-section-/, ''));
  }

  function sectionDetail(section) {
    return { area: area(section), section: sectionId(section), position: [...sections.keys()].indexOf(section) + 1 };
  }

  function visible(element) {
    const box = element.getBoundingClientRect();
    const style = getComputedStyle(element);
    return box.width > 0 && box.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
  }

  function scan() {
    if (!config.homepage || !main || !observer) return;
    main.querySelectorAll('.shopify-section,[data-mf-nav-section]').forEach(section => {
      if (sections.has(section)) return;
      const candidates = [...section.querySelectorAll('[data-mf-nav-view],h1,h2,h3,a[href],img')];
      const anchor = candidates.find(visible) || section;
      sections.set(section, anchor);
      observer.observe(anchor);
    });
  }

  function clearTimers() {
    timers.forEach(timer => clearTimeout(timer));
    timers.clear();
  }

  function consentChanged() {
    clearTimers();
    if (!allowed()) return;
    if (config.homepage && !homeRecorded) homeRecorded = emit('mf_home_view', { area: 'homepage' });
    if (observer) {
      sections.forEach(anchor => { observer.unobserve(anchor); observer.observe(anchor); });
    }
  }

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const section = [...sections].find(([, anchor]) => anchor === entry.target)?.[0];
        if (!section) return;
        const key = sectionId(section);
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || !allowed() || document.hidden) {
          clearTimeout(timers.get(key)); timers.delete(key); return;
        }
        if (seen.has(key) || timers.has(key)) return;
        timers.set(key, setTimeout(() => {
          timers.delete(key);
          if (!allowed() || document.hidden || seen.has(key)) return;
          const box = entry.target.getBoundingClientRect();
          if (box.bottom <= 0 || box.top >= innerHeight || !visible(entry.target)) return;
          if (emit('mf_home_section_view', sectionDetail(section))) seen.add(key);
        }, 1000));
      });
    }, { threshold: 0.5 });
    scan();
    if (main) new MutationObserver(() => {
      clearTimeout(scanTimer); scanTimer = setTimeout(scan, 200);
    }).observe(main, { childList: true, subtree: true });
  }

  function searchRoot(element) {
    return element.closest('[data-mf-search-results],.predictive-search,[id*="SearchAutoComplete"],.gspf-search-results,[role="listbox"]');
  }

  function detailFor(link) {
    const href = link.getAttribute('href')?.trim();
    if (!href || href.startsWith('#')) return null;
    const target = safePath(link.href);
    if (!target || target === '/search') return null;
    const desktop = link.closest('[data-navbar-builder]');
    const mobile = link.closest('[data-mobile-menu]');
    let detail;
    if (desktop) {
      const item = link.closest('[data-navbar-item]');
      detail = { area: 'desktop_menu', section: item?.dataset.mfNavItem || 'desktop', item: target, target };
    } else if (mobile) {
      detail = { area: 'mobile_menu', section: safePath(mobile.querySelector('[data-sub-title]')?.href) || 'mobile', item: target, target };
    } else if (searchRoot(link)) {
      detail = { area: 'predictive_search', section: 'predictive_search', item: target, target };
    } else if (main?.contains(link) && context() === 'search') {
      detail = { area: 'search_results', section: 'search_results', item: target, target };
    } else if (main?.contains(link) && config.homepage) {
      const section = sectionFor(link);
      detail = section ? { ...sectionDetail(section), item: target, target }
        : { area: 'unclassified_homepage', section: 'none', item: target, target };
    } else if (link.closest('footer,.footer')) {
      detail = { area: 'footer', section: 'footer', item: target, target };
    } else if (link.closest('header')) {
      detail = { area: 'header', section: 'header', item: target, target };
    } else if (main?.contains(link)) {
      detail = { area: 'content', section: 'content', item: target, target };
    } else return null;
    const root = searchRoot(link) || sectionFor(link) || desktop || mobile || main;
    const targets = [...(root?.querySelectorAll('a[href]') || [])].map(a => safePath(a.href)).filter(Boolean);
    detail.position = [...new Set(targets)].indexOf(target) + 1;
    return detail;
  }

  document.addEventListener('click', event => {
    const element = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!element) return;
    const trigger = element.closest('[data-navbar-trigger]');
    if (trigger && trigger.getAttribute('aria-expanded') === 'true') {
      emit('mf_menu_open', { area: 'desktop_menu', section: trigger.closest('[data-navbar-item]')?.dataset.mfNavItem || 'desktop', item: 'menu' });
    }
    const hamburger = element.closest('[data-hamburger]');
    if (hamburger && document.querySelector('[data-mobile-menu]')?.classList.contains('is-active')) {
      emit('mf_menu_open', { area: 'mobile_menu', section: 'mobile', item: 'menu' });
    }
    const drill = element.closest('[data-drill-btn]');
    if (drill) emit('mf_menu_open', { area: 'mobile_menu', section: safePath(drill.dataset.url) || 'mobile_submenu', item: 'submenu' });
    const searchToggle = element.closest('[data-mobile-search-toggler]');
    if (searchToggle) setTimeout(() => {
      const form = document.getElementById('mobileSearchHolder');
      if (form && visible(form)) emit('mf_search_open', { area: 'search', section: 'mobile_search', item: 'search' });
    }, 0);
    const link = element.closest('a[href]');
    if (!link) return;
    const detail = detailFor(link);
    if (!detail) return;
    const section = config.homepage && main?.contains(link) ? sectionFor(link) : null;
    if (section && !seen.has(sectionId(section)) && emit('mf_home_section_view', sectionDetail(section))) {
      seen.add(sectionId(section));
    }
    const isResult = ['predictive_search', 'search_results'].includes(detail.area);
    emit(isResult ? 'mf_search_result_click' : 'mf_navigation_click', detail);
  });

  document.addEventListener('focusin', event => {
    if (!(event.target instanceof Element) || !event.target.matches('input[type="search"]')) return;
    const form = event.target.closest('form[action*="/search"]');
    if (!form || form.id === 'mobileSearchHolder' || openedSearch.has(form)) return;
    if (emit('mf_search_open', { area: 'search', section: 'desktop_search', item: 'search' })) openedSearch.add(form);
  });

  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearTimers(); else consentChanged();
  });
  if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
      if (!error) consentChanged();
    });
  }
  consentChanged();
})();
